'use client';
import NextImage from 'next/image';
import { Icon, Link, Tag } from 'ui';
import { type TestimonialCardsProps } from './index';

const TestimonialCardSingle = ({ cards }: TestimonialCardsProps) => {
  const card = cards?.[0];

  // Render null if no card is present
  if (!card) {
    return null;
  }

  return (
    <div className="flex flex-col md:flex-row-reverse md:h-125">
      {/* Card Image */}
      <div className="relative overflow-hidden h-71.5 md:w-1/2 md:h-auto md:shrink-0">
        {/* Background Image */}
        <div className="absolute inset-0 flex flex-col justify-end p-5 lg:p-8 rounded-t-lg md:rounded-r-lg md:rounded-tl-none overflow-hidden">
          <NextImage
            src={card.image.src}
            alt={card.image.alt || ''}
            fill={true}
            className="absolute z-1 inset-0 size-full object-cover"
            role="presentation"
          />

          {/* Logo */}
          {card.logo && (
            <>
              <NextImage
                src={card.logo.src}
                alt={`${card.authorOrg || 'Company'} logo`}
                width={card.logo.width}
                height={card.logo.height}
                className="relative z-3 max-w-full h-auto"
              />

              {/* Color Gradient */}
              <div
                className="absolute z-2 bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-black/65 to-transparent"
                aria-hidden="true"
              />
            </>
          )}
        </div>
      </div>

      {/* Card Content */}
      <div className="overflow-hidden md:w-1/2 md:shrink-0">
        <article className="size-full p-6 lg:p-8 bg-stone-800 rounded-b-lg md:rounded-l-lg md:rounded-br-none">
          <div className="h-full flex flex-col gap-6 delay-400 transition-all duration-300 opacity-100 translate-x-0">
            <div className="grow">{!!card.tag && <Tag>{card.tag}</Tag>}</div>

            <blockquote className="flex flex-col gap-4">
              <p className="text-h5">
                {!card.hideLeftQuote && (
                  <>
                    &ldquo; <br />
                  </>
                )}
                {card.content}
                {!card.hideRightQuote && <>&rdquo;</>}
              </p>

              <footer className="flex flex-col gap-8">
                {(!!card.author || !!card.authorTitle || !!card.authorOrg) && (
                  <cite className="text-p2 font-bold not-italic text-neutral-200 flex flex-col opacity-85">
                    {(card.author || card.authorTitle) && (
                      <span>
                        {card.author && <span>{card.author}</span>}
                        {card.authorTitle && (
                          <>
                            {card.author && ', '}
                            <span>{card.authorTitle}</span>
                          </>
                        )}
                        {card.authorOrg && (
                          <>
                            {(card.author || card.authorTitle) && <br />}
                            <span>{card.authorOrg}</span>
                          </>
                        )}
                      </span>
                    )}
                  </cite>
                )}

                {!!card.link?.href && (
                  <div className="text-p2">
                    <Link
                      href={card.link.href}
                      target={card.link.target}
                      variant="neutral"
                    >
                      <span>{card.link.text}</span>
                      <Icon
                        name="carousel-arrow-right"
                        className="inline-block align-middle ml-1.5 w-4 h-auto"
                      />
                    </Link>
                  </div>
                )}
              </footer>
            </blockquote>
          </div>
        </article>
      </div>
    </div>
  );
};

export default TestimonialCardSingle;
